// oxlint-disable prefer-global-this, no-use-before-define
import { Service, signal } from '@angular/core';

type ConsentConfiguration = Readonly<{
  marketing: boolean;
  statistics: boolean;
}>;

type ConsentCookieName = 'fb_consent' | 'mtm_consent' | 'mtm_consent_removed';

type ConsentState = Readonly<{
  hasStoredChoice: boolean;
  marketing: boolean;
  statistics: boolean;
}>;

type FbqArguments = ReadonlyArray<unknown>;

type FbqFunction = {
  (...args: FbqArguments): void;
  callMethod?: (...args: FbqArguments) => void;
  loaded?: boolean;
  push?: FbqFunction;
  queue?: Array<FbqArguments>;
  version?: string;
};

type MatomoQueue = Array<ReadonlyArray<string>>;

type WritableConsentCookieName = 'fb_consent' | 'mtm_consent_removed';

// const FACEBOOK_PIXEL_ID = '373910441719828';
// const FACEBOOK_SCRIPT_SELECTOR =
//   'script[data-tiia-consent-script="facebook-pixel"]';
const MATOMO_BASE_URL = 'https://analytika.kbpojistovna.cz/';
const MATOMO_SCRIPT_SELECTOR = 'script[data-tiia-consent-script="matomo"]';

type ConsentWindow = {
  _fbq?: FbqFunction;
  _paq?: MatomoQueue;
  fbq?: FbqFunction;
} & typeof globalThis;

const consentWindow = globalThis as ConsentWindow;

// const runFacebookQueue: FbqFunction = (...args: FbqArguments): void => {
//   const { fbq } = consentWindow;

//   if (!fbq) {
//     return;
//   }

//   if (fbq.callMethod) {
//     fbq.callMethod(...args);

//     return;
//   }

//   fbq.queue?.push(args);
// };

const startFB = (): void => {
  // if (consentWindow.fbq || hasTrackingScript(FACEBOOK_SCRIPT_SELECTOR)) {
  //   return;
  // }
  // const fbq = createFacebookQueue();
  // consentWindow.fbq = fbq;
  // consentWindow._fbq ??= fbq;
  // insertTrackingScript(
  //   createTrackingScript(
  //     'https://connect.facebook.net/en_US/fbevents.js',
  //     'facebook-pixel',
  //   ),
  // );
  // consentWindow.fbq('init', FACEBOOK_PIXEL_ID);
  // consentWindow.fbq('track', 'PageView');
};

const startMatomo = (): void => {
  consentWindow._paq ??= [];

  if (hasTrackingScript(MATOMO_SCRIPT_SELECTOR)) {
    return;
  }

  // queueMatomoCommands([
  //   ['trackPageView'],
  //   ['enableLinkTracking'],
  //   ['setTrackerUrl', `${MATOMO_BASE_URL}matomo.php`],
  //   ['setSiteId', '9'],
  // ]);

  insertTrackingScript(
    createTrackingScript(
      `${MATOMO_BASE_URL}js/container_9JC04seX.js`,
      'matomo',
    ),
  );
};

const applyStoredConsent = (consent: ConsentState): void => {
  if (consent.statistics) {
    startMatomo();
    rememberMatomoConsent();
  }

  if (consent.marketing) {
    startFB();
  }
};

const getConsentConfiguration = (
  config?: ConsentConfiguration,
): ConsentConfiguration => ({
  marketing: true,
  statistics: true,
  ...config,
});

const getStoredConsentState = (): ConsentState => {
  if (hasCookie('mtm_consent_removed')) {
    return {
      hasStoredChoice: true,
      marketing: false,
      statistics: false,
    };
  }

  const marketing = hasCookie('fb_consent');
  const statistics = hasCookie('mtm_consent');

  return {
    hasStoredChoice: marketing || statistics,
    marketing,
    statistics,
  };
};

const isDeniedConsent = (consent: ConsentConfiguration): boolean =>
  !consent.marketing && !consent.statistics;

@Service()
class ConsentService {
  readonly #consentState = signal(getStoredConsentState());
  readonly showConfig = signal(false);
  readonly showConsent = signal(!this.#consentState().hasStoredChoice);

  readonly consentState = this.#consentState.asReadonly();

  constructor() {
    applyStoredConsent(this.#consentState());
  }

  #setConsentState(consentState: ConsentState): void {
    this.#consentState.set(consentState);
  }

  allow(config?: ConsentConfiguration): void {
    const consent = getConsentConfiguration(config);

    if (isDeniedConsent(consent)) {
      this.deny();

      return;
    }

    syncConsent(consent);
    this.#setConsentState({
      hasStoredChoice: true,
      marketing: consent.marketing,
      statistics: consent.statistics,
    });
    this.showConsent.set(false);
    this.showConfig.set(false);
  }

  deny(): void {
    clearCookie('fb_consent');
    clearCookie('mtm_consent');
    setCookie('mtm_consent_removed');
    this.#setConsentState({
      hasStoredChoice: true,
      marketing: false,
      statistics: false,
    });
    this.showConsent.set(false);
    this.showConfig.set(false);
  }

  openConfig(): void {
    this.showConfig.set(true);
  }
}

function clearCookie(type: ConsentCookieName): void {
  cookieStore.delete({ name: type, path: '/' });
}

// function createFacebookQueue(): FbqFunction {
//   const fbq = runFacebookQueue;
//   fbq.push = fbq;
//   fbq.loaded = true;
//   fbq.version = '2.0';
//   fbq.queue = [];

//   return fbq;
// }

function createTrackingScript(
  source: string,
  scriptName: 'facebook-pixel' | 'matomo',
): HTMLScriptElement {
  const script = document.createElement('script');
  script.async = true;
  script.src = source;
  script.dataset['tiiaConsentScript'] = scriptName;

  return script;
}

function hasCookie(name: ConsentCookieName): boolean {
  return document.cookie
    .split(';')
    .some(cookie => cookie.trim().startsWith(`${name}=`));
}

function hasTrackingScript(selector: string): boolean {
  return document.querySelector(selector) !== null;
}

function insertTrackingScript(script: HTMLScriptElement): void {
  const firstScript = document.querySelector('script');

  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);

    return;
  }

  document.head.append(script);
}

function queueMatomoCommands(
  commands: ReadonlyArray<ReadonlyArray<string>>,
): void {
  consentWindow._paq = [...(consentWindow._paq ?? []), ...commands];
}

function rememberMatomoConsent(): void {
  queueMatomoCommands([['rememberConsentGiven']]);
}

function setCookie(
  type: WritableConsentCookieName,
  expirationDays = 365,
): void {
  const timestamp = Date.now().toString();
  const expirationDate = new Date();
  const expirationMilliseconds = expirationDays * (24 * 60 * 60 * 1000);

  expirationDate.setTime(expirationDate.getTime() + expirationMilliseconds);

  cookieStore.set({
    expires: expirationDate.getTime(),
    name: type,
    path: '/',
    sameSite: 'strict',
    value: timestamp,
  });
}

function syncConsent(consent: ConsentConfiguration): void {
  clearCookie('mtm_consent_removed');
  syncMarketingConsent(consent.marketing);
  syncStatisticsConsent(consent.statistics);
}

function syncMarketingConsent(marketing: boolean): void {
  if (marketing) {
    setCookie('fb_consent');
    startFB();

    return;
  }

  clearCookie('fb_consent');
}

function syncStatisticsConsent(statistics: boolean): void {
  if (statistics) {
    startMatomo();
    rememberMatomoConsent();

    return;
  }

  clearCookie('mtm_consent');
}

export { type ConsentConfiguration, ConsentService, type ConsentState };
